import React from 'react';
import { useI18n } from '../contexts/I18nContext';
import { Stars, Heart, Flower } from 'lucide-react';
import { APP_SUPPORTERS } from '../utils/app-supporters';

const TikTokIcon = ({ size = 12 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    className="text-slate-700"
  >
    <path
      fill="currentColor"
      d="M14 3c.6 1.8 2 3.3 3.8 3.9 1 .3 2.1.4 3.2.3v2.8c-2.4.1-4.7-.7-6.6-2.2v7.2a5.8 5.8 0 1 1-5.8-5.8c.4 0 .7 0 1.1.1v3a2.7 2.7 0 1 0 1.6 2.4V3h2.7z"
    />
  </svg>
);

const InstagramIcon = ({ size = 12 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    className="text-slate-700"
  >
    <path
      fill="currentColor"
      d="M12 7.3A4.7 4.7 0 1 0 12 16.7 4.7 4.7 0 0 0 12 7.3m0 7.7A3 3 0 1 1 12 9a3 3 0 0 1 0 6m6-7.9a1.1 1.1 0 1 1-1.1-1.1 1.1 1.1 0 0 1 1.1 1.1M12 3.8c2.3 0 2.6 0 3.5.1.8 0 1.2.2 1.5.3.4.2.7.4 1 .8.3.3.6.6.8 1 .2.3.3.7.3 1.5.1.9.1 1.2.1 3.5s0 2.6-.1 3.5c0 .8-.2 1.2-.3 1.5-.2.4-.4.7-.8 1-.3.3-.6.6-1 .8-.3.2-.7.3-1.5.3-.9.1-1.2.1-3.5.1s-2.6 0-3.5-.1c-.8 0-1.2-.2-1.5-.3-.4-.2-.7-.4-1-.8-.3-.3-.6-.6-.8-1-.2-.3-.3-.7-.3-1.5-.1-.9-.1-1.2-.1-3.5s0-2.6.1-3.5c0-.8.2-1.2.3-1.5.2-.4.4-.7.8-1 .3-.3.6-.6 1-.8.3-.2.7-.3 1.5-.3.9-.1 1.2-.1 3.5-.1m0-1.8c-2.3 0-2.6 0-3.6.1-1 0-1.7.2-2.3.5a3.9 3.9 0 0 0-1.4.9 3.9 3.9 0 0 0-.9 1.4c-.3.6-.5 1.3-.5 2.3-.1 1-.1 1.3-.1 3.6s0 2.6.1 3.6c0 1 .2 1.7.5 2.3.2.5.5 1 .9 1.4.4.4.9.7 1.4.9.6.3 1.3.5 2.3.5 1 .1 1.3.1 3.6.1s2.6 0 3.6-.1c1 0 1.7-.2 2.3-.5.5-.2 1-.5 1.4-.9.4-.4.7-.9.9-1.4.3-.6.5-1.3.5-2.3.1-1 .1-1.3.1-3.6s0-2.6-.1-3.6c0-1-.2-1.7-.5-2.3a3.9 3.9 0 0 0-.9-1.4 3.9 3.9 0 0 0-1.4-.9c-.6-.3-1.3-.5-2.3-.5-1-.1-1.3-.1-3.6-.1Z"
    />
  </svg>
);

const SupportersTicker = ({ supporters }) => {
  const supporterList = supporters.length > 0 ? supporters : [{ handle: '@supporter', platform: '' }];
  const supporterCount = supporterList.length;

  // Repeat the list enough times to fill any screen width without over-rendering
  const repeatCount = Math.max(4, Math.ceil(36 / supporterList.length));
  const extendedList = Array.from({ length: repeatCount }, () => supporterList).flat();
  const baseGap = Math.max(8, Math.round(48 - supporterCount * 2));
  const desktopGap = Math.max(22, Math.round(96 - supporterCount * 3));

  // Render one set of items
  const renderItems = (keyPrefix) =>
    extendedList.map((supporter, idx) => (
      <span
        key={`${keyPrefix}-${idx}`}
        className="ticker-item inline-flex items-center gap-1.5 text-xs text-slate-600 font-medium shrink-0"
      >
        {supporter.platform === '' && (
          <Heart size={10} className="text-rose-400 fill-rose-400" />
        )}
        {supporter.platform === 'tiktok' && <TikTokIcon size={11} />}
        {supporter.platform === 'instagram' && <InstagramIcon size={11} />}
        <span>{supporter.handle}</span>
        <Heart size={10} className="text-rose-400 fill-rose-400" />
      </span>
    ));

  return (
    <div className="w-full overflow-hidden pointer-events-none select-none">
      <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 text-center pt-3">
        MarkerSync Supporters
      </div>
      <div
        className="ticker-track py-2"
        style={{
          '--ticker-duration': '180s',
          '--ticker-duration-lg': '200s',
          '--ticker-gap': `${baseGap}px`,
          '--ticker-gap-lg': `${desktopGap}px`,
        }}
      >
        <div className="ticker-half">
          {renderItems('a')}
        </div>
        <div className="ticker-half">
          {renderItems('b')}
        </div>
      </div>
    </div>
  );
};

const WeeklyBanner = () => {
  const { t } = useI18n();

  return (
    <div className="relative overflow-hidden bg-white border-b border-yellow-100/70 pt-[90px]">
      <div
        className="absolute inset-0 bg-cover bg-[center_30%] opacity-15 pointer-events-none"
        style={{ backgroundImage: "url('/images/summer-vibes.jpg')", backgroundSize: '50% auto'}}
        aria-hidden
      />
      <div className="relative max-w-6xl mx-auto px-6 py-2">
        <div className="flex items-center justify-center">
          <div className="flex items-center space-x-6">
            {/* <div className="hidden md:flex h-14 w-14 rounded-2xl bg-gradient-to-br from-emerald-200 to-rose-200 items-center justify-center shadow-elegant-lg border border-white/70 backdrop-blur">
              <span className="text-2xl" aria-hidden>🎄</span>
            </div> */}
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <h3 className="text-lg font-bold text-yellow-900 tracking-tight">
                  {t('weeklyChallenge.title')}
                </h3>
                <span className="text-lg font-semibold text-yellow-500 inline-flex items-center space-x-2">
                  <Flower size={22} className="inline-block ml-2 text-yellow-500" />
                  <span>{t('weeklyChallenge.currentTheme')}</span>
                </span>
              </div>
              <p className="text-sm sm:text-base text-yellow-900/80 max-w-3xl mx-auto">
                {t('weeklyChallenge.description')}
                <span className='px-1 text-yellow-500'>{t('weeklyChallenge.hashtag')}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Supporters Ticker */}
      <SupportersTicker supporters={APP_SUPPORTERS} />
    </div>
  );
};

export default WeeklyBanner;
